/**
 * Search string extraction, filtering, and screening data display formatting
 */
import { formatPatientName, formatStatusDisplay } from './formatters';

/**
 * Safely extract location string for filtering and search
 * @param {any} location - Location data (could be object, string, or null)
 * @returns {string} - Searchable location string
 */
export const getLocationSearchString = (location) => {
  if (!location) return '';
  
  if (typeof location === 'string') {
    return location.toLowerCase();
  }
  
  if (typeof location === 'object') {
    const parts = [];
    
    if (location.name) parts.push(location.name);
    if (location.title) parts.push(location.title);
    if (location.address) {
      if (typeof location.address === 'string') {
        parts.push(location.address);
      } else if (typeof location.address === 'object') {
        if (location.address.street) parts.push(location.address.street);
        if (location.address.city) parts.push(location.address.city);
        if (location.address.state) parts.push(location.address.state);
        if (location.address.zipCode) parts.push(location.address.zipCode);
      }
    }
    if (location.city) parts.push(location.city);
    if (location.state) parts.push(location.state);
    if (location.street) parts.push(location.street);
    
    return parts.join(' ').toLowerCase();
  }
  
  try {
    return location.toString().toLowerCase();
  } catch (error) {
    return '';
  }
};

/**
 * Safely extract event string for filtering and search
 * @param {any} event - Event data (could be object, string, or null)
 * @returns {string} - Searchable event string
 */
export const getEventSearchString = (event) => {
  if (!event) return '';
  
  if (typeof event === 'string') {
    return event.toLowerCase();
  }
  
  if (typeof event === 'object') {
    const parts = [];
    
    if (event.title) parts.push(event.title);
    if (event.name) parts.push(event.name);
    if (event.description) parts.push(event.description);
    if (event.type) parts.push(event.type);
    if (event.id) parts.push(event.id);
    
    return parts.join(' ').toLowerCase();
  }
  
  try {
    return event.toString().toLowerCase();
  } catch (error) {
    return '';
  }
};

/**
 * Safely extract patient name for filtering and search
 * @param {Object} patient - Patient data object
 * @returns {string} - Searchable patient name string
 */
export const getPatientSearchString = (patient) => {
  if (!patient) return '';
  
  const parts = [];
  
  if (patient.firstName) parts.push(patient.firstName);
  if (patient.lastName) parts.push(patient.lastName);
  if (patient.name) parts.push(patient.name);
  if (patient.patientId) parts.push(patient.patientId);
  if (patient.id) parts.push(patient.id);
  if (patient.emailAddress) parts.push(patient.emailAddress);
  if (patient.phoneNumber) parts.push(patient.phoneNumber);
  
  return parts.join(' ').toLowerCase();
};

/**
 * Filter screening data with safe property access
 * @param {Array} screenings - Array of screening data
 * @param {string} searchTerm - Search term to filter by
 * @param {Object} filters - Additional filters (status, event, location, etc.)
 * @returns {Array} - Filtered screening data
 */
export const filterScreeningData = (screenings, searchTerm = '', filters = {}) => {
  if (!Array.isArray(screenings)) return [];
  
  const normalizedSearchTerm = String(searchTerm || '').toLowerCase().trim();
  
  return screenings.filter(screening => {
    // Text search across multiple fields
    if (normalizedSearchTerm) {
      const searchableText = [
        getPatientSearchString(screening),
        getEventSearchString(screening.eventId || screening.event),
        getLocationSearchString(screening.location || screening.eventId?.location),
        screening.status || '',
        screening.queueStatus || '',
        screening.testingCode || '',
        screening.id || '',
        screening.patientId || ''
      ].join(' ').toLowerCase();
      
      if (!searchableText.includes(normalizedSearchTerm)) {
        return false;
      }
    }
    
    // Status filter
    if (filters.status && filters.status !== 'all') {
      const screeningStatus = (screening.status || screening.queueStatus || '').toLowerCase();
      const filterStatus = String(filters.status || '').toLowerCase();
      if (!screeningStatus.includes(filterStatus)) {
        return false;
      }
    }
    
    // Event filter
    if (filters.eventId && filters.eventId !== 'all') {
      const eventId = screening.eventId?.id || screening.eventId?._id || screening.eventId;
      if (eventId !== filters.eventId) {
        return false;
      }
    }
    
    // Location filter
    if (filters.location && filters.location !== 'all') {
      const locationString = getLocationSearchString(screening.location || screening.eventId?.location);
      const filterLocation = String(filters.location || '').toLowerCase();
      if (!locationString.includes(filterLocation)) {
        return false;
      }
    }
    
    // Date range filter
    if (filters.startDate || filters.endDate) {
      const screeningDate = new Date(screening.createdAt || screening.checkInTime || screening.timestamp);
      
      if (filters.startDate && screeningDate < new Date(filters.startDate)) {
        return false;
      }
      
      if (filters.endDate && screeningDate > new Date(filters.endDate)) {
        return false;
      }
    }
    
    return true;
  });
};

/**
 * Get unique values from screening data for filter options
 * @param {Array} screenings - Array of screening data
 * @returns {Object} - Object with arrays of unique values for filters
 */
export const getScreeningFilterOptions = (screenings) => {
  if (!Array.isArray(screenings)) {
    return {
      statuses: [],
      events: [],
      locations: []
    };
  }
  
  const statuses = new Set();
  const events = new Set();
  const locations = new Set();
  
  screenings.forEach(screening => {
    if (screening.status) statuses.add(screening.status);
    if (screening.queueStatus) statuses.add(screening.queueStatus);
    
    const event = screening.eventId || screening.event;
    if (event) {
      if (typeof event === 'string') {
        events.add(event);
      } else if (event.title) {
        events.add(event.title);
      } else if (event.name) {
        events.add(event.name);
      }
    }
    
    const location = screening.location || screening.eventId?.location;
    if (location) {
      const locationString = getLocationSearchString(location);
      if (locationString) {
        locations.add(locationString);
      }
    }
  });
  
  return {
    statuses: Array.from(statuses).sort(),
    events: Array.from(events).sort(),
    locations: Array.from(locations).sort()
  };
};

/**
 * Format screening data for display, handling all object properties safely
 * @param {Array} screenings - Raw screening data from API
 * @returns {Array} - Formatted screening data for display
 */
export const formatScreeningDataForDisplay = (screenings) => {
  if (!Array.isArray(screenings)) return [];
  
  return screenings.map(screening => {
    if (!screening) return null;
    
    const eventInfo = screening.eventId || screening.event;
    const locationInfo = screening.location || eventInfo?.location;
    
    return {
      ...screening,
      patientName: formatPatientName(screening),
      eventTitle: getEventSearchString(eventInfo) || 'Unknown Event',
      locationName: getLocationSearchString(locationInfo) || 'Unknown Location',
      statusDisplay: formatStatusDisplay(screening.status || screening.queueStatus),
      
      createdAtFormatted: screening.createdAt ? 
        new Date(screening.createdAt).toLocaleDateString() : 'No date',
      checkInTimeFormatted: screening.checkInTime ? 
        new Date(screening.checkInTime).toLocaleString() : 'No check-in time',
      
      originalEvent: eventInfo,
      originalLocation: locationInfo,
      
      searchableText: [
        getPatientSearchString(screening),
        getEventSearchString(eventInfo),
        getLocationSearchString(locationInfo),
        screening.status || '',
        screening.testingCode || ''
      ].join(' ').toLowerCase()
    };
  }).filter(Boolean);
};
